import { ArrowRight, DownloadSimple, GitPullRequest, ShieldCheck } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { CatalogToolbar, type CatalogFilterState } from "../components/CatalogToolbar.js";
import { ThemeCard } from "../components/ThemeCard.js";
import { catalog } from "../data/catalog.js";
import { text } from "../i18n.js";
import { COMMUNITY_REPOSITORY, localeHref } from "../lib/routes.js";
import type { CommunityThemeView, Locale } from "../types.js";

const defaults: CatalogFilterState = { query: "", tag: "", status: "", featured: false, sort: "featured" };

function initialFilters(): CatalogFilterState {
  if (typeof window === "undefined") return defaults;
  const params = new URLSearchParams(window.location.search);
  const sort = params.get("sort");
  const status = params.get("status");
  return {
    query: params.get("q") ?? "",
    tag: params.get("tag") ?? "",
    status: ["verified", "needs-reverification", "incompatible", "yanked"].includes(status ?? "")
      ? status as CatalogFilterState["status"] : "",
    featured: params.get("featured") === "1",
    sort: sort === "newest" || sort === "updated" ? sort : "featured",
  };
}

function releaseTime(theme: CommunityThemeView, mode: "newest" | "updated"): number {
  const version = mode === "updated" ? theme.versions[0] : theme.versions.at(-1);
  return version ? Date.parse(version.releasedAt) : 0;
}

export function DirectoryPage({ locale }: { readonly locale: Locale }) {
  const t = text(locale);
  const [filters, setFilters] = useState(initialFilters);
  const tags = useMemo(() => [...new Set(catalog.themes.flatMap((theme) => theme.tags))].sort(), []);
  const results = useMemo(() => {
    const query = filters.query.trim().toLocaleLowerCase(locale);
    return catalog.themes.filter((theme) => {
      const version = filters.status
        ? theme.versions.find(({ supportStatus }) => supportStatus === filters.status)
        : theme.versions.find(({ version }) => version === theme.latestVersion);
      if (!version) return false;
      if (filters.featured && !theme.featured) return false;
      if (filters.tag && !theme.tags.includes(filters.tag)) return false;
      if (query) {
        const content = theme.localized[locale];
        const haystack = [content.name, content.summary, theme.author.name, theme.author.github, ...theme.tags]
          .join(" ").toLocaleLowerCase(locale);
        if (!haystack.includes(query)) return false;
      }
      return true;
    }).sort((left, right) => {
      if (filters.sort === "featured" && left.featured !== right.featured) return left.featured ? -1 : 1;
      if (filters.sort !== "featured") return releaseTime(right, filters.sort) - releaseTime(left, filters.sort);
      return left.localized[locale].name.localeCompare(right.localized[locale].name, locale);
    });
  }, [filters, locale]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.query) params.set("q", filters.query);
    if (filters.tag) params.set("tag", filters.tag);
    if (filters.status) params.set("status", filters.status);
    if (filters.featured) params.set("featured", "1");
    if (filters.sort !== "featured") params.set("sort", filters.sort);
    const search = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${search ? `?${search}` : ""}`);
  }, [filters]);

  return (
    <>
      <section className="directory-hero">
        <div className="container hero-grid">
          <div className="hero-copy">
            <h1>{t.heroTitle}</h1>
            <p>{t.heroBody}</p>
            <a className="primary-button" href="#catalog">{t.catalogTitle}<ArrowRight aria-hidden="true" weight="bold" /></a>
          </div>
          <div className="hero-motif" aria-hidden="true">
            <div className="motif-window motif-window-back"><i /><i /><i /></div>
            <div className="motif-window motif-window-front"><span /><span /><b /></div>
          </div>
        </div>
      </section>
      <section className="catalog-section" id="catalog">
        <div className="container">
          <CatalogToolbar locale={locale} filters={filters} tags={tags} onChange={setFilters} />
          <div className="catalog-heading">
            <h2>{t.catalogTitle}</h2>
            <p aria-live="polite">{t.resultCount(results.length)}</p>
          </div>
          {catalog.themes.length === 0 ? (
            <div className="empty-state">
              <GitPullRequest aria-hidden="true" weight="thin" />
              <h3>{t.emptyTitle}</h3><p>{t.emptyBody}</p>
              <a className="secondary-button" href={localeHref(locale, "submit")}>{t.nav.submit}<ArrowRight aria-hidden="true" /></a>
            </div>
          ) : results.length === 0 ? (
            <div className="empty-state compact"><h3>{t.noResultsTitle}</h3><p>{t.noResultsBody}</p></div>
          ) : (
            <div className="theme-grid">{results.map((theme) => <ThemeCard key={theme.id} locale={locale} theme={theme} />)}</div>
          )}
        </div>
      </section>
      <section className="pathway-section">
        <div className="container pathway-grid">
          <div className="pathway-intro">
            <h2>{locale === "zh-CN" ? "从公开审核到本地导入" : "From public review to local import"}</h2>
            <p>{locale === "zh-CN" ? "社区仓库保存源目录与审核记录；GitHub Release 保存不可变归档；Theme Studio 负责最终本地校验。" : "The community repository keeps source and review history; GitHub Releases hold immutable archives; Theme Studio performs the final local validation."}</p>
          </div>
          <ol className="pathway-steps">
            <li><GitPullRequest aria-hidden="true" /><span><strong>01</strong>{locale === "zh-CN" ? "通过 Pull Request 投稿并公开审核" : "Submit and review through a Pull Request"}</span></li>
            <li><ShieldCheck aria-hidden="true" /><span><strong>02</strong>{locale === "zh-CN" ? "可信 CI 校验、打包并生成哈希" : "Trusted CI validates, packages and hashes"}</span></li>
            <li><DownloadSimple aria-hidden="true" /><span><strong>03</strong>{locale === "zh-CN" ? "下载后在 Theme Studio 手动导入" : "Download and import manually in Theme Studio"}</span></li>
          </ol>
        </div>
      </section>
      <section className="community-band">
        <div className="container community-band-grid">
          <div><h2>{locale === "zh-CN" ? "把你的主题带到社区" : "Bring your theme to the community"}</h2><p>{t.submitLead}</p></div>
          <div className="band-actions">
            <a className="light-button" href={localeHref(locale, "submit")}>{t.nav.submit}<ArrowRight aria-hidden="true" /></a>
            <a className="band-link" href={`${COMMUNITY_REPOSITORY}/fork`} target="_blank" rel="noreferrer">Fork on GitHub</a>
          </div>
        </div>
      </section>
    </>
  );
}
